import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useIntl } from '@edx/frontend-platform/i18n';
import { Spinner, StatefulButton } from '@edx/paragon';
import { useNavigate, useParams } from 'react-router';

import {
  otpTimer,
  otpTimerDecrease,
  validateOtp,
  validateOtpReset,
  // validateOtpReset,
} from './data/actions';
import { OTP_LENGTH, OTP_STATE } from './data/constants';
import messages from './messages';
import { OtpSuccessSVG, CheckCircleSVG } from '../assets/svgs';
import ArrowButton from '../common-components/ArrowButton';
import Decorations from '../common-components/Decorations';
import ResetPasswordStepsTabs from '../common-components/ResetPasswordStepsTabs';
import VerificationCodeInput from '../common-components/VerificationCodeInput';
import { RESET_PAGE, PASSWORD_RESET_CODE_ENTRY, PENDING_STATE } from '../data/constants';
import { forgotPassword } from '../forgot-password/data/actions';

const ForgotPasswordCodePage = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [showSendAgain, setShowSendAgain] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);

  const {
    status,
    resetLink,
    timer: {
      isLoading,
      isTimer,
      remainingSeconds,
      email: emailFromTimer,
    },
  } = useSelector((state) => state.forgotPasswordCode);

  // Populated after a successful forgot-password request (PT 5.8, recommendation
  // #1): the in-app "Enter Code" button identifies its OTP session by this email
  // instead of a server-issued token, since that response no longer carries one.
  // `status` is also watched below so a "resend" - a second successful request -
  // re-triggers the timer/status check even when it doesn't navigate anywhere.
  const { email: emailFromRequest, status: forgotPasswordStatus } = useSelector((state) => state.forgotPassword);

  const { token } = useParams();

  // Once the status/timer check succeeds we also know the email straight from the
  // record itself; fall back to what the user originally submitted otherwise.
  const email = emailFromTimer || emailFromRequest;
  const identifier = useMemo(() => (token ? { token } : { email }), [token, email]);

  const { formatMessage } = useIntl();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const verificationCodeInputRef = useRef();

  const isCompleteStatus = status === OTP_STATE.COMPLETE;
  const isFailureStatus = status === OTP_STATE.FAILURE;

  const handleVerificationChange = (newCode) => {
    setVerificationCode(newCode);
  };

  const handleVerificationSubmit = () => {
    if (verificationCode.length < OTP_LENGTH) {
      return;
    }

    if (!isCompleteStatus) {
      dispatch(validateOtp(verificationCode, identifier));
    } else {
      navigate(new URL(resetLink).pathname.replace('/authn', ''));
    }
  };

  useEffect(() => {
    if (isFailureStatus) {
      setFailedAttempts((prev) => prev + 1);
    }
  }, [isFailureStatus]);

  const handleSendAgain = () => {
    if (email) {
      verificationCodeInputRef.current.reset();
      setVerificationCode('');
      setFailedAttempts(0);
      dispatch(validateOtpReset());
      dispatch(forgotPassword(email));
      setShowSendAgain(false);
      // A resend invalidates whatever token this page may have been loaded with,
      // and the response no longer returns a new one to follow (PT 5.8,
      // recommendation #1). Switch to the email-keyed route/endpoints instead of
      // trying to track a moving target.
      if (token) {
        navigate(PASSWORD_RESET_CODE_ENTRY);
      }
    } else {
      navigate(RESET_PAGE);
    }
  };

  useEffect(() => {
    if (!token && !email) {
      // Landed on the in-app entry route with no email in Redux state (e.g. a
      // page refresh) and no token either - nothing to check against.
      navigate(RESET_PAGE);
      return;
    }
    if (forgotPasswordStatus === PENDING_STATE) {
      // A resend is in flight (dispatch(forgotPassword(email)) in
      // handleSendAgain) - wait for it to actually create the new code
      // server-side before re-checking status, rather than querying the
      // about-to-be-replaced old record.
      return;
    }
    // Depends on forgotPasswordStatus, not just token/dispatch: on the in-app
    // (tokenless) entry route, a "resend" doesn't navigate anywhere - it just
    // triggers a second successful forgotPassword request - so this needs to
    // re-fire on that transition too, or the page gets stuck showing the
    // isLoading state that validateOtpReset() put it back into (previously a
    // bug: resend from this route left the page blank/stuck loading forever).
    dispatch(otpTimer(identifier));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token, forgotPasswordStatus]);

  useEffect(() => {
    if (!isTimer || isCompleteStatus) {
      return;
    }

    if (remainingSeconds === 0) {
      setTimeout(() => {
        setShowSendAgain(true);
      }, 3000);
      return;
    }

    const timerId = setInterval(() => {
      dispatch(otpTimerDecrease());
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(timerId);
    };
  }, [isTimer, remainingSeconds, isCompleteStatus, dispatch]);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      dispatch(validateOtpReset());
    };
  }, [dispatch]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const remainSeconds = time % 60;
    return `${minutes}:${remainSeconds < 10 ? `0${remainSeconds}` : remainSeconds}`;
  };

  const isInvalid = remainingSeconds === 0 || failedAttempts >= 3;

  if (isLoading) {
    return <Spinner animation="border" variant="primary" className="spinner--position-centered" />;
  }

  return (
    <div className="d-flex justify-content-between flex-column forgot-password-code flex-fill position-relative">
      <div className="d-flex align-items-center pb-5 flex-column flex-fill justify-content-between my-6 align-self-center overflow-hidden">
        <ResetPasswordStepsTabs step={0} />
        <div className="d-flex align-items-center flex-column my-5">
          <h1 className="page-title">{formatMessage(messages['verification.code.page.title'])}</h1>
          <div className='reset-password-warper'>
            {!isCompleteStatus && (
              <p className="w-100 my-3">{formatMessage(messages['verification.code.page.subtitle'])}</p>
            )}
            <VerificationCodeInput
              ref={verificationCodeInputRef}
              isInvalid={isFailureStatus && remainingSeconds > 0}
              isSuccess={isCompleteStatus}
              onComplete={handleVerificationChange}
              className="mt-5"
            />
            {isCompleteStatus && (
              <div className="d-flex flex-row w-100 my-3 align-items-center">
                <CheckCircleSVG className="otp-success-icon" color="#323333" backgroundColor="#91C653" ariaLabel="otp-success" />
                <p className="success-text mb-0 mx-2">{formatMessage(messages['verification.code.success.text'])}</p>
              </div>
            )}
            {!isCompleteStatus && (
              <div className="w-100 mt-3 d-flex justify-content-between flex-wrap">
                {(isFailureStatus && remainingSeconds > 0) && <span className="text-danger small-text-bold">{formatMessage(messages['verification.code.input.invalid.value'])}</span>}
                {(remainingSeconds === 0) && <span className="text-danger small-text-bold">{formatMessage(messages['verification.code.input.expired.value'])}</span>}
                {showSendAgain && (
                  <button
                  type="button"
                  className="text-right timer link-style small-text-bold"
                  onClick={handleSendAgain}
                  >
                    {formatMessage(messages['verification.code.page.get.new.code'])}
                  </button>
                )}
                {!showSendAgain && <span className="text-right timer small-text-bold">{formatTime(remainingSeconds)}</span>}
              </div>
            )}
          </div>
        </div>
        <div className="w-100 mt-5 flex-center-row buttons-wrapper gap-20">
          <ArrowButton onClick={() => navigate(RESET_PAGE)} />
          <StatefulButton
            id="submit-new-password"
            name="submit-new-password"
            type="submit"
            className="logistration-btn"
            state={isInvalid ? 'disabled' : 'default'}
            disabled={isInvalid}
            labels={{
              default: !isCompleteStatus
                ? formatMessage(messages['verification.code.page.button'])
                : formatMessage(messages['verification.code.page.choose.new.password.button']),
              pending: '',
            }}
            onClick={handleVerificationSubmit}
          />
        </div>
      </div>
      <Decorations />
    </div>
  );
};

export default ForgotPasswordCodePage;
